// src/services/scheduleService.ts
import type { Vaccine, DoseDefinition, VaccinationRecord, CalendarEventParams } from '../types';
import { useVaccineDefinitions } from '../stores/useVaccineDefinitions';
import { getDisplayPlannedDate } from '../utils/vaccineDate';

/* =========================================================
   查詢函數（從 Pinia store 讀取，支援 Firestore 動態更新）
========================================================= */

export function getVaccineDetails(vaccineId: string): Vaccine | undefined {
  return useVaccineDefinitions().getVaccineById(vaccineId);
}

export function getDoseDetails(doseId: string): DoseDefinition | undefined {
  return useVaccineDefinitions().getDoseById(doseId);
}

export function getVaccineDoses(vaccineId: string): DoseDefinition[] {
  return useVaccineDefinitions().getDosesByVaccineId(vaccineId);
}

export function getPublicVaccines(): Vaccine[] {
  return useVaccineDefinitions().getAllVaccines().filter(v => v.type === 'public' || v.type === 'mixed');
}

export function getSelfPayVaccines(): Vaccine[] {
  return useVaccineDefinitions().getAllVaccines().filter(v => v.type === 'self' || v.type === 'mixed');
}

/* =========================================================
   Google Calendar 相關
========================================================= */

/**
 * 產生 Google Calendar 事件 URL
 */
export function generateCalendarUrl(
  record: VaccinationRecord,
  babyNickname: string
): string | null {
  const vaccine = getVaccineDetails(record.vaccineId);
  const dose = getDoseDetails(record.doseId);

  if (!vaccine || !dose) {
    console.error('Vaccine or dose not found');
    return null;
  }

  const title = `[預約疫苗] ${babyNickname} - ${vaccine.name} ${dose.label}`;
  
  const description = [
    `疫苗效果: ${vaccine.description || '預防相關疾病'}`,
    '',
    `常見反應: ${vaccine.sideEffects?.join('、') || '請參考醫師說明'}`,
    '',
    '請記得攜帶兒童健康手冊'
  ].join('\n');

  const displayDate = getDisplayPlannedDate(record);
  const params: CalendarEventParams = {
    title,
    description,
    startDate: displayDate,
    endDate: displayDate,
    reminderMinutes: 1440 // 提前一天提醒
  };

  return buildGoogleCalendarUrl(params);
}

/**
 * 建立 Google Calendar URL
 */
function buildGoogleCalendarUrl(params: CalendarEventParams): string {
  const baseUrl = 'https://calendar.google.com/calendar/render';
  
  // 格式化日期為 Google Calendar 格式 (YYYYMMDD)
  const formatDateForGoogle = (dateStr: string): string => {
    return dateStr.replace(/-/g, '');
  };

  const queryParams = new URLSearchParams({
    action: 'TEMPLATE',
    text: params.title,
    dates: `${formatDateForGoogle(params.startDate)}/${formatDateForGoogle(params.endDate || params.startDate)}`,
    details: params.description || '',
    location: params.location || '',
    // Google Calendar 的提醒格式
    // add: 'reminder', // 這會加入預設提醒
  });

  return `${baseUrl}?${queryParams.toString()}`;
}

/**
 * RFC 5545 ICS 文字欄位轉義（SUMMARY、DESCRIPTION 等 TEXT 屬性）
 */
function escapeICSText(text: string): string {
  return text
    .replace(/\r/g, '')          // 移除 CR
    .replace(/\\/g, '\\\\')      // 反斜線（須最先處理）
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');      // 換行轉為 ICS 轉義序列
}

/**
 * 產生 ICS (iCalendar) 內容，用於 Apple Calendar（RFC 5545）
 */
export function generateICSContent(
  record: VaccinationRecord,
  babyNickname: string
): string | null {
  const vaccine = getVaccineDetails(record.vaccineId);
  const dose = getDoseDetails(record.doseId);

  if (!vaccine || !dose) return null;

  const title = escapeICSText(`[預約疫苗] ${babyNickname} - ${vaccine.name} ${dose.label}`);
  const descriptionLines = [
    `疫苗效果: ${vaccine.description || '預防相關疾病'}`,
    `常見反應: ${vaccine.sideEffects?.join('、') || '請參考醫師說明'}`,
    '請記得攜帶兒童健康手冊',
  ];
  const description = escapeICSText(descriptionLines.join('\n'));

  const displayDate = getDisplayPlannedDate(record);
  const dateStr = displayDate.replace(/-/g, '');
  // RFC 5545: 全天事件的 DTEND 為排除端（exclusive），需加一天
  // 使用 Date.UTC 避免本地時區影響日期計算（如 UTC+8 會導致日期偏移）
  const dateParts = displayDate.split('-').map(Number);
  const dtendDate = new Date(Date.UTC(dateParts[0]!, dateParts[1]! - 1, dateParts[2]! + 1));
  const dtendStr = dtendDate.toISOString().slice(0, 10).replace(/-/g, '');

  const uid = `vaccine-${record.id}@babyvaccinehelper`;
  const dtstamp = new Date().toISOString().replace(/[-:.]/g, '').slice(0, 15) + 'Z';

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BabyVaccineHelper//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;VALUE=DATE:${dateStr}`,
    `DTEND;VALUE=DATE:${dtendStr}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'DESCRIPTION:疫苗提醒',
    'TRIGGER:-P1D',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

/* =========================================================
   日期計算輔助函數
========================================================= */

/**
 * 計算年齡（以月為單位）
 */
export function calculateAgeInMonths(birthDate: string, targetDate: string = new Date().toISOString()): number {
  const birth = new Date(birthDate);
  const target = new Date(targetDate);
  
  const yearDiff = target.getFullYear() - birth.getFullYear();
  const monthDiff = target.getMonth() - birth.getMonth();
  
  return yearDiff * 12 + monthDiff;
}

/**
 * 計算年齡（以天為單位）
 */
export function calculateAgeInDays(birthDate: string, targetDate: string = new Date().toISOString()): number {
  const birth = new Date(birthDate);
  const target = new Date(targetDate);
  
  const diffTime = target.getTime() - birth.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * 格式化年齡顯示
 */
export function formatAge(birthDate: string): string {
  const months = calculateAgeInMonths(birthDate);
  
  if (months < 12) {
    return `${months}個月`;
  }
  
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  
  if (remainingMonths === 0) {
    return `${years}歲`;
  }
  
  return `${years}歲${remainingMonths}個月`;
}

/**
 * 格式化日期顯示 (YYYY/MM/DD)
 */
export function formatDate(dateStr: string): string {
  return dateStr.replace(/-/g, '/');
}

/**
 * 格式化日期顯示 (中文)
 */
export function formatDateChinese(dateStr: string): string {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  return `${year}年${month}月${day}日`;
}

/* =========================================================
   統計函數
========================================================= */

/**
 * 計算接種完成率
 */
export function calculateCompletionRate(records: VaccinationRecord[]): number {
  if (records.length === 0) return 0;
  
  const completed = records.filter(r => r.status === 'completed').length;
  return Math.round((completed / records.length) * 100);
}

/**
 * 取得即將到期的疫苗 (未來7天內)
 */
export function getUpcomingVaccinations(records: VaccinationRecord[]): VaccinationRecord[] {
  const now = new Date();
  const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  return records.filter(r => {
    if (r.status !== 'planned') return false;

    const displayDate = new Date(getDisplayPlannedDate(r));
    return displayDate >= now && displayDate <= sevenDaysLater;
  });
}

/**
 * 取得逾期的疫苗
 */
export function getOverdueVaccinations(records: VaccinationRecord[]): VaccinationRecord[] {
  return records.filter(r => r.status === 'overdue');
}

/**
 * 按疫苗類型分組統計
 */
export function getStatsByType(records: VaccinationRecord[]): {
  public: { total: number; completed: number };
  self: { total: number; completed: number };
} {
  const stats = {
    public: { total: 0, completed: 0 },
    self: { total: 0, completed: 0 }
  };

  records.forEach(record => {
    const vaccine = getVaccineDetails(record.vaccineId);
    if (!vaccine) return;

    const type = vaccine.type === 'mixed' ? 'public' : vaccine.type;
    
    stats[type].total++;
    if (record.status === 'completed') {
      stats[type].completed++;
    }
  });

  return stats;
}